import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, FileCode, FileDown, BookOpen, ExternalLink } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;

const DOWNLOADS = [
  {
    title: "Guia de Integração MV Delphi",
    desc: "Documentação completa em PDF: arquitetura, autenticação, SDK, exemplos end-to-end, webhooks, endpoints REST, FAQ.",
    icon: FileText, href: `${API}/docs/erp-delphi.pdf`,
    badge: "PDF · 15 seções", testid: "doc-pdf",
  },
  {
    title: "Guia de Integração MV Delphi (Markdown)",
    desc: "Mesma documentação em formato Markdown — útil para versionamento no Git e visualização in-app.",
    icon: BookOpen, href: `${API}/docs/erp-delphi.md`,
    badge: ".MD", testid: "doc-md",
  },
  {
    title: "SDK Delphi — Up2FinanceHub.pas",
    desc: "Unit única para Delphi 10.4 Sydney+ / RAD Studio 11/12. Sem dependências externas — apenas RTL nativa.",
    icon: FileCode, href: `${API}/docs/sdk-delphi.pas`,
    badge: ".PAS", testid: "doc-sdk",
  },
];

export default function Documentacao() {
  return (
    <div className="space-y-6" data-testid="documentacao-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Recursos</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Documentação</h1>
        <p className="text-sm text-zinc-500 mt-1">Guias, SDK e referências para integrar seu MV ao UP2 Finance Hub.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DOWNLOADS.map((d) => (
          <Card key={d.testid} className="rounded-sm border border-zinc-200 shadow-none p-6 flex flex-col" data-testid={d.testid}>
            <div className="flex items-start justify-between mb-4">
              <d.icon className="w-8 h-8 text-zinc-700" strokeWidth={1.5} />
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 px-2 py-0.5 border border-zinc-200 rounded-sm">
                {d.badge}
              </span>
            </div>
            <h3 className="font-display text-lg font-semibold mb-1">{d.title}</h3>
            <p className="text-sm text-zinc-600 flex-1">{d.desc}</p>
            <a href={d.href} target="_blank" rel="noreferrer" className="mt-4" data-testid={`${d.testid}-link`}>
              <Button variant="outline" className="rounded-sm w-full">
                <FileDown className="w-4 h-4 mr-2" /> Baixar
              </Button>
            </a>
          </Card>
        ))}

        <Card className="rounded-sm border border-zinc-200 shadow-none p-6 flex flex-col md:col-span-2" data-testid="doc-swagger">
          <div className="flex items-start justify-between mb-4">
            <ExternalLink className="w-8 h-8 text-zinc-700" strokeWidth={1.5} />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 px-2 py-0.5 border border-zinc-200 rounded-sm">
              OpenAPI
            </span>
          </div>
          <h3 className="font-display text-lg font-semibold mb-1">Swagger UI — Referência interativa da API</h3>
          <p className="text-sm text-zinc-600 flex-1">Documentação OpenAPI gerada automaticamente pelo FastAPI. Permite testar endpoints diretamente no navegador.</p>
          <a href={`${BASE}/docs`} target="_blank" rel="noreferrer" className="mt-4" data-testid="doc-swagger-link">
            <Button variant="outline" className="rounded-sm w-full">
              <ExternalLink className="w-4 h-4 mr-2" /> Abrir Swagger
            </Button>
          </a>
        </Card>
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-3">Quick start MV Delphi</div>
        <pre className="text-[11px] bg-zinc-50 border border-zinc-200 rounded-sm p-4 overflow-x-auto font-mono leading-relaxed">{`uses Up2FinanceHub, System.JSON;

var Hub: TUp2FinanceHub;
Hub := TUp2FinanceHub.Create('${BASE}');
try
  Hub.Login('user@empresa.com', 'minhasenha');

  var Boleto := TJSONObject.Create;
  Boleto.AddPair('bank_code', '077');
  Boleto.AddPair('numero_documento', 'NF-1234');
  Boleto.AddPair('origem', 'erp');
  // Para avulso, use origem='avulso' e deixe nosso_numero vazio para o Hub gerar pela sequência do banco.
  Boleto.AddPair('valor', TJSONNumber.Create(350.00));
  Boleto.AddPair('vencimento', '2026-07-10');
  Boleto.AddPair('cliente',
    TJSONObject.Create
      .AddPair('nome', 'JOAO DA SILVA')
      .AddPair('cpf_cnpj', '12345678901'));

  ShowMessage(Hub.EmitirBoleto(Boleto).GetValue<string>('linha_digitavel'));
finally
  Hub.Free;
end;`}</pre>
      </Card>
    </div>
  );
}
